import ggoLogger from '@/modules/ggo-logger'
import { LogName, SearchLogData } from '@/interfaces/Logger'
import { SearchQueryObject } from '@/composables/use-search-bar'
import SearchFilter, { SortMethod } from '@/classes/SearchFilter'
import Coordinate from '@/interfaces/Coordinate'
import HertzSearchFilter from '@/classes/Hertz/HertzSearchFilter'

// ----- Logger Search 的型態 -----
/** 搜索相關數據 */
export interface LoggerSearchInformation {
  startLat: Coordinate['lat'];
  startLng: Coordinate['lng'];
  startLocation: string;

  endLat: number;
  endLng: number;
  endLocation: string;

  startDate: string;
  endDate: string;

  startHour: number;
  endHour: number;
}

export enum OrderBy {
  DISTANCE_ASC = 'distance_asc',
  DISTANCE_DESC = 'distance_desc',
  PRICE_ASC = 'price_asc',
  PRICE_DESC = 'price_desc',
  RATE_DESC = 'rating_desc'
}
export interface LoggerSearchFilter {
  /** 排序方式 */
  orderBy?: OrderBy;
  /** 座位數 */
  seatMin?: number;
  seatMax?: number;
  /** 品牌 */
  brandIds?: number[];
  /** 車齡 */
  ageMin?: number;
  ageMax?: number;
  /** 是否接受國際駕照 */
  intlDrivingPermit?: boolean;
  /** 付款方式 */
  paymentMethodIds?: number[];
  /** 價格 */
  priceMin?: number;
  priceMax?: number;
  /** 車型 */
  carTypeIds?: number[];
  /** 店家服務 */
  serviceIds?: number[];
  /** 車輛設備 */
  equipmentIds?: number[];
}
// ----- End Logger Search 的型態 -----

// ----- Logger Search 型態轉換 -----
const transformSearchInformation = (search: SearchQueryObject): LoggerSearchInformation => {
  return {
    // 強轉型
    startLat: Number(search.lat),
    // 強轉型
    startLng: Number(search.lng),
    startLocation: String(search.location),
    endLat: search.returnLat ? Number(search.returnLat) : Number(search.lat),
    endLng: search.returnLng ? Number(search.returnLng) : Number(search.lng),
    endLocation: search.returnLocation ? search.returnLocation : String(search.location),
    startDate: String(search.startDate),
    startHour: Number(search.startHour),
    endDate: String(search.endDate),
    endHour: Number(search.endHour),
  }
}
const transformOrderBy = (sortMethod?: SortMethod): OrderBy | undefined => {
  if (!sortMethod) return

  switch (sortMethod) {
    case 'distanceAsc':
      return OrderBy.DISTANCE_ASC
    case 'distanceDesc':
      return OrderBy.DISTANCE_DESC
    case 'priceAsc':
      return OrderBy.PRICE_ASC
    case 'priceDesc':
      return OrderBy.PRICE_DESC
    case 'ratingDesc':
      return OrderBy.RATE_DESC
  }
}
const isGGOSearchFilter = (filter: SearchFilter | HertzSearchFilter): filter is SearchFilter => {
  filter = filter as SearchFilter

  return !!filter.brands
}
const transformSearchFilter = (filter: SearchFilter | HertzSearchFilter): LoggerSearchFilter => {
  return {
    orderBy: transformOrderBy(filter.sortMethod),
    seatMax: filter.seatMax,
    seatMin: filter.seatMin,
    intlDrivingPermit: filter.acceptForeigner,
    ...(filter.priceRange && {
      priceMin: filter.priceRange[0],
      priceMax: filter.priceRange[1],
    }),
    ...(isGGOSearchFilter(filter) && {
      brandIds: filter.brands,
      ...(filter.ageRange && {
        ageMin: Number(filter.ageRange.split(',')[0]),
        ageMax: Number(filter.ageRange.split(',')[1]),
      }),
      paymentMethodIds: filter.paymentMethods,
      carTypeIds: filter.carTypes,
      serviceIds: filter.storeServices,
      equipmentIds: filter.equipments,
    }),
  }
}

export default function () {
  /** 日誌 key map. key: 篩選器字段名, value: 日誌字段名 */
  const filterLogKeyNameMap = {
    priceMin: 'price_min',
    priceMax: 'price_max',
    seatMin: 'seat_min',
    seatMax: 'seat_max',
    ageMin: 'age_min',
    ageMax: 'age_max',
    brandIds: 'brand_ids',
    intlDrivingPermit: 'intl_driving_permit',
    paymentMethodIds: 'payment_method_ids',
    carTypeIds: 'car_type_ids',
    serviceIds: 'service_ids',
    equipmentIds: 'equipment_ids',
    orderBy: 'order_by',
  }
  // 在這個 set 的字段, 只有數值為 true 才會記錄
  const onlyTrueLogKey = new Set(['intlDrivingPermit'])
  /** 記錄搜尋日誌 */
  const searchLog = ({ search: searchObject, filter: searchFilter, storeCnt, vehicleCnt, qid }: {
    /** 搜尋條件 */
    search: SearchQueryObject;
    /** 篩選器 */
    filter?: SearchFilter | HertzSearchFilter;
    /** 店家數 */
    storeCnt: number;
    /** 車輛數 */
    vehicleCnt: number;
    // 推薦系統分辨每次查詢的 query id
    qid?: string
  }) => {
    const search = transformSearchInformation(searchObject)
    let filter
    if (searchFilter) {
      filter = transformSearchFilter(searchFilter)
    }

    const formattedFilter = <SearchLogData['filter']>{}
    if (filter && formattedFilter) {
    // 格式化篩選器
      Object.entries(filter).forEach(([key, value]) => {
      // 將 SearchFilter 的 key 轉換為搜尋日誌的 key
        const logKey = filterLogKeyNameMap[key as keyof typeof filterLogKeyNameMap] as keyof typeof formattedFilter | undefined
        if (logKey) {
        // 跳過 在 `onlyTrueLogKey` 但值不為 true 的字段
          if (onlyTrueLogKey.has(key) && !value) {
            return
          }
          formattedFilter[logKey] = value
        }
      })
    }

    ggoLogger.info(LogName.SEARCH, {
      search_location: {
        start_latitude: search.startLat,
        start_longitude: search.startLng,
        start_location: search.startLocation,
        end_latitude: search.endLat,
        end_longitude: search.endLng,
        end_location: search.endLocation,
      },
      datetime: {
        start_date: search.startDate,
        start_time: `${search.startHour}:00`,
        end_date: search.endDate,
        end_time: `${search.endHour}:00`,
      },
      ...(filter && { filter: formattedFilter }),
      result: {
        store_count: storeCnt,
        vehicle_count: vehicleCnt,
        qid,
      },
    })
  }

  return {
    searchLog,
  }
}
